import React, { useState, lazy, Suspense } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button } from "@humansignal/ui";
import { IconGrid, IconList, IconPlus } from "@humansignal/icons";
import { Oneof } from "../../components/Oneof/Oneof";
import { Loading } from "../../components/Loading/Loading";
import { ApiContext } from "../../providers/ApiProvider";
import { useAuth } from "@humansignal/core/providers/AuthProvider";
import { cn } from "../../utils/bem";
// Lazy-loaded: CreateProject only renders inside the "New project" modal
// (opened on a button click), and it drags in the whole config editor —
// codemirror (~515 KB) + the Config UI. Splitting it into its own chunk
// keeps that off the first-paint bundle for everyone who isn't actively
// creating a project. Rendered under <Suspense> below.
const CreateProject = lazy(() => import("../CreateProject/CreateProject").then((m) => ({ default: m.CreateProject })));
import { DataManagerPage } from "../DataManager/DataManager";
import { ExportPreviewPage } from "../ExportPage/ExportPreviewPage";
import { ValidatePage } from "../Validate/ValidatePage";
import { SettingsPage } from "../Settings";
import { ReviewStreamPage } from "../Review/ReviewStream";
import { BulkLabelPage } from "../BulkLabel/BulkLabel";
import { ProjectDashboard } from "../ProjectDashboard/ProjectDashboard";
import { ProjectAnalytics } from "../ProjectDashboard/ProjectAnalytics";
import { MembersSettings } from "../Settings/MembersSettings";
import { EmptyProjectsList, ProjectsList } from "./ProjectsList";
import { DiscoverPage } from "../Discover/DiscoverPage";
import { useAbortController, useUpdatePageTitle } from "@humansignal/core";
import { useOrgQuota } from "../../hooks/useOrgQuota";
import { useTranslation } from "../../i18n/LocaleProvider";
import { isCapacitor } from "../../utils/host";
import "./Projects.prefix.css";

const getCurrentPage = () => {
  const pageNumberFromURL = new URLSearchParams(location.search).get("page");

  return pageNumberFromURL ? Number.parseInt(pageNumberFromURL) : 1;
};

export const ProjectsPage = () => {
  const { t } = useTranslation();
  const api = React.useContext(ApiContext);
  const abortController = useAbortController();
  const [projectsList, setProjectsList] = React.useState([]);
  const [networkState, setNetworkState] = React.useState(null);
  const [currentPage, setCurrentPage] = useState(getCurrentPage());
  const [totalItems, setTotalItems] = useState(1);
  const { user } = useAuth();
  const canCreateProjects = user?.is_staff || ["OW", "AD", "MA"].includes(user?.active_organization_role);
  const quota = useOrgQuota();
  const atProjectLimit = !!quota?.atProjects;

  useUpdatePageTitle("Projects");
  const defaultPageSize = Number.parseInt(localStorage.getItem("pages:projects-list") ?? 30);

  const [modal, setModal] = React.useState(false);
  // Orgless users (no active org, not staff) land on Discover by default —
  // "My projects" is empty for them. Everyone else defaults to their
  // projects. Discover + Projects are two tabs of one surface now.
  const isOrgless = !!user?.id && !user?.active_organization && !user?.is_staff;
  // The active tab IS the URL: bare ``/discover`` = Discover tab,
  // ``/projects`` = My projects. Directly openable / linkable like the
  // app's other tabs. Orgless users are routed to ``/discover`` by the
  // shell (see RootPage), so this derivation covers their default too.
  const history = useHistory();
  const location = useLocation();
  // Orgless users have no "My projects" tab, so always show Discover for
  // them even if they land on /projects.
  const activeTab = location.pathname === "/discover" || isOrgless ? "discover" : "projects";
  const selectTab = React.useCallback((tab) => history.push(tab === "discover" ? "/discover" : "/projects"), [history]);
  const [workspaces, setWorkspaces] = React.useState([]);
  const [workspaceFilter, setWorkspaceFilter] = React.useState("all"); // "all" | "none" | <id>
  const [projectQuery, setProjectQuery] = React.useState(""); // My-projects search, as typed
  // What the server was last asked to search for (every project, not only
  // the page in view). A ref too, so the fetch reads the latest.
  const [search, setSearch] = React.useState("");
  const searchRef = React.useRef("");
  // Mirrors the Data Manager's grid/list toggle — persists across
  // sessions so the user's preference survives reloads.
  const [view, setView] = React.useState(() => localStorage.getItem("projects-list:view") ?? "grid");
  React.useEffect(() => {
    localStorage.setItem("projects-list:view", view);
  }, [view]);

  React.useEffect(() => {
    api.callApi("workspaces", { suppressError: true }).then((res) => {
      if (res && !res.error) setWorkspaces(res.results ?? []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openModal = () => setModal(true);

  const closeModal = () => setModal(false);

  // Grid view has a fixed, viewport-sized page (no per-page picker):
  // 12 cards on desktop, fewer as the screen narrows. List view keeps
  // the user-chosen page size.
  const gridPageSize = useGridPageSize();
  const effectivePageSize = view === "grid" ? gridPageSize : defaultPageSize;

  const fetchProjects = async (page = currentPage, pageSize = effectivePageSize, quiet = false) => {
    // A search keeps the page in place: swapping it for the loader would
    // take the search box away from under the person typing.
    if (!quiet) setNetworkState("loading");
    abortController.renew(); // Cancel any in flight requests

    const requestParams = { page, page_size: pageSize };
    // "Archived" is a list mode, not a workspace: the server keeps
    // archived projects out of every listing unless asked.
    if (workspaceFilter === "archived") requestParams.archived = true;
    if (searchRef.current) requestParams.search = searchRef.current;

    requestParams.include = [
      "id",
      "title",
      "created_by",
      "created_at",
      "color",
      "is_published",
      "is_archived",
      "assignment_settings",
      "state",
      "workspace",
      // Tier-badge needs these to render the right icon + decide
      // whether the click-to-toggle path is available. Without them
      // the badge falls back to a static span (cloud_id=undefined ⇒
      // togglable=false), which kills the tooltip and the toggle.
      "cloud_id",
      "local_sync_tier",
      "offline_pref_enabled",
      "allow_offline_for_annotators",
      "allow_metadata_mirror",
      // Mobile gate — without this the Capacitor filter below has no
      // field to read and shows zero projects.
      "allow_on_mobile",
    ].join(",");

    const data = await api.callApi("projects", {
      params: requestParams,
      signal: abortController.controller.current.signal,
      errorFilter: (e) => e.error.includes("aborted"),
    });

    // Capacitor mobile bundle: only show projects whose owners have
    // opted into mobile via ``Project.allow_on_mobile``. Annotation
    // / review surface on phone is intentionally restricted; owners
    // toggle the flag in General Settings when their task type
    // works on a small screen. Web + desktop are unaffected.
    const allResults = data?.results ?? [];
    // ``allow_on_mobile === false`` hides; ``undefined`` (a mirrored row
    // written from a projection that omitted the field) must NOT hide —
    // treating unknown as false made projects blink out between
    // navigations while the mirror caught up.
    const filtered = isCapacitor() ? allResults.filter((p) => p.allow_on_mobile !== false) : allResults;
    setTotalItems(isCapacitor() ? filtered.length : (data?.count ?? 1));
    setProjectsList(filtered);
    setNetworkState("loaded");

    if (filtered.length) {
      const additionalData = await api.callApi("projects", {
        params: {
          // Use the FILTERED ids on Capacitor — otherwise the merge
          // below replaces the filtered state with unfiltered data
          // and projects that weren't in our list (no allow_on_mobile)
          // sneak back in but missing fields the second include
          // doesn't fetch (created_at, title, etc.). That broke the
          // project card render with a "RangeError: invalid time value"
          // from ``format(new Date(undefined))``.
          ids: filtered.map(({ id }) => id).join(","),
          include: [
            "id",
            "description",
            "num_tasks_with_annotations",
            "task_number",
            "skipped_annotations_number",
            "total_annotations_number",
            "total_predictions_number",
            "ground_truth_number",
            "finished_task_number",
            // What installed extensions say about each project; its
            // ``keywords`` are matched by the search box.
            "extensions",
          ].join(","),
          page_size: pageSize,
        },
        signal: abortController.controller.current.signal,
        errorFilter: (e) => e.error.includes("aborted"),
      });

      if (additionalData?.results?.length) {
        const filteredIds = new Set(filtered.map((p) => p.id));
        setProjectsList((prev) =>
          additionalData.results
            // Defensive — second filter in case the cloud returns ids
            // we didn't ask for (paginators sometimes leak adjacent
            // pages). Drops anything not in the original mobile-allowed
            // set so the merge can never resurrect a stripped project.
            .filter((project) => filteredIds.has(project.id))
            .map((project) => {
              const prevProject = prev.find(({ id }) => id === project.id);

              return {
                ...prevProject,
                ...project,
              };
            }),
        );
      }
    }
  };

  const loadNextPage = async (page, pageSize) => {
    setCurrentPage(page);
    // While a search is on, the page stays in place (see fetchProjects):
    // the pager asks for page 1 again when the results change.
    await fetchProjects(page, pageSize, Boolean(searchRef.current));
  };

  // Re-fetch when the operator switches active org. Without
  // ``user?.active_organization`` in the deps the projects list
  // keeps showing whichever org's results loaded first — visible
  // on desktop where the afri_sync proxy can flip
  // ``user.active_organization`` without forcing a hard reload.
  // Switching grid ↔ list (or resizing across a grid breakpoint) changes
  // the page size: go back to page 1 with the new size.
  const firstSizeRun = React.useRef(true);
  React.useEffect(() => {
    if (firstSizeRun.current) {
      firstSizeRun.current = false;
      return;
    }
    setCurrentPage(1);
    fetchProjects(1, effectivePageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectivePageSize]);

  React.useEffect(() => {
    setProjectsList([]);
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.active_organization]);

  // The search box asks the server, a moment after the typing stops.
  React.useEffect(() => {
    const wanted = projectQuery.trim();
    if (wanted === searchRef.current) return;
    const timer = setTimeout(() => {
      searchRef.current = wanted;
      setSearch(wanted);
      setCurrentPage(1);
      fetchProjects(1, effectivePageSize, true);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectQuery]);

  // Archived is served by a different query (``?archived=true``), so
  // flipping into or out of it refetches; other chips filter client-side.
  const archivedMode = workspaceFilter === "archived";
  const archivedModeRef = React.useRef(archivedMode);
  React.useEffect(() => {
    if (archivedModeRef.current === archivedMode) return;
    archivedModeRef.current = archivedMode;
    setProjectsList([]);
    setCurrentPage(1);
    fetchProjects(1, effectivePageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archivedMode]);

  const showCreateButton = projectsList.length > 0 && canCreateProjects;
  const createTooltip = atProjectLimit
    ? `Project limit reached (${quota?.usage?.projects?.current}/${quota?.usage?.projects?.max}). Contact a platform administrator to raise the quota.`
    : undefined;

  return (
    <div className={cn("projects-page").toClassName()}>
      {/* Page header — anchors the tab row to the page instead of
          letting it drift against the menubar. Title + short subtitle
          establish context; the primary Create action lives here so
          the tab row below is pure sub-navigation. */}
      <header className={cn("projects-page").elem("header").toClassName()}>
        <div className={cn("projects-page").elem("header-text").toClassName()}>
          <h1 className={cn("projects-page").elem("header-title").toClassName()}>Projects</h1>
          <p className={cn("projects-page").elem("header-subtitle").toClassName()}>
            {canCreateProjects
              ? "Create, manage, and publish projects for your organization."
              : "Projects you have access to in this organization."}
          </p>
        </div>
      </header>

      {/* One surface, two tabs: your org's projects and the public
          Discover feed. Shown for everyone (orgless users default to
          Discover). Templates moved out to Organization + "My templates"
          in the profile menu. */}
      <div className={cn("projects-page").elem("tabs").toClassName()}>
        <div className={cn("projects-page").elem("tabs-list").toClassName()}>
          {/* "My projects" only for users who are in an organization —
              orgless users have no org projects. "Discover" is always
              available (public projects are cross-org). */}
          {!isOrgless && (
            <button
              type="button"
              className={cn("projects-page")
                .elem("tab")
                .mod({ active: activeTab === "projects" })
                .toClassName()}
              onClick={() => selectTab("projects")}
            >
              My projects
            </button>
          )}
          <button
            type="button"
            className={cn("projects-page")
              .elem("tab")
              .mod({ active: activeTab === "discover" })
              .toClassName()}
            onClick={() => selectTab("discover")}
          >
            Discover
          </button>
        </div>
        {/* View toggle lives on the right of the tabs row. Shared by
            both tabs — Discover renders the same cards, so the same
            grid/list preference applies there too. */}
        {(activeTab === "discover" || projectsList.length > 0) && (
          <div
            className={cn("projects-page").elem("view-toggle").toClassName()}
            role="group"
            aria-label={t("pages.projects.projects.view_mode")}
          >
            <button
              type="button"
              className={cn("projects-page")
                .elem("view-btn")
                .mod({ active: view === "list" })
                .toClassName()}
              onClick={() => setView("list")}
              aria-label={t("pages.projects.projects.switch_to_list_view")}
              aria-pressed={view === "list"}
              title={t("pages.projects.projects.list_view")}
            >
              <IconList />
            </button>
            <button
              type="button"
              className={cn("projects-page")
                .elem("view-btn")
                .mod({ active: view === "grid" })
                .toClassName()}
              onClick={() => setView("grid")}
              aria-label={t("pages.projects.projects.switch_to_grid_view")}
              aria-pressed={view === "grid"}
              title={t("pages.projects.projects.grid_view")}
            >
              <IconGrid />
            </button>
          </div>
        )}
      </div>

      {activeTab === "discover" ? (
        <DiscoverPage embedded view={view} />
      ) : (
        <Oneof value={networkState}>
          <div className={cn("projects-page").elem("loading").toClassName()} case="loading">
            <Loading />
          </div>
          <div className={cn("projects-page").elem("content").toClassName()} case="loaded">
            {/* The filters stay while one of them is why the list is empty:
                without them there would be no way back. */}
            {(projectsList.length > 0 || search || archivedMode) && (
              <div className={cn("projects-page").elem("toolbar").toClassName()}>
                {/* "All projects" + "Unassigned" always render — they
                    are meaningful even in orgs that haven't created
                    any named workspaces. Custom workspaces append
                    after when they exist. */}
                <div className={cn("projects-page").elem("ws-filter").toClassName()}>
                  {[
                    { key: "all", label: "All projects" },
                    { key: "none", label: "Unassigned" },
                    ...workspaces.map((w) => ({ key: String(w.id), label: w.title, color: w.color })),
                    ...(canCreateProjects ? [{ key: "archived", label: "Archived" }] : []),
                  ].map((f) => (
                    <button
                      key={f.key}
                      type="button"
                      className={cn("projects-page")
                        .elem("ws-chip")
                        .mod({ active: workspaceFilter === f.key })
                        .toClassName()}
                      onClick={() => setWorkspaceFilter(f.key)}
                    >
                      {f.color && (
                        <span
                          style={{
                            display: "inline-block",
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: f.color,
                            marginRight: 6,
                          }}
                        />
                      )}
                      {f.label}
                    </button>
                  ))}
                </div>
                {/* Spacer pushes the search (and the view toggle) to the
                    right edge of the toolbar. */}
                <div style={{ flex: "1 1 auto" }} aria-hidden />
                {/* Search: every project of the organisation, by title or
                    by a word an extension knows it by. Styled via CSS (not inline) so the phone rules can
                    stretch it full-width when the toolbar wraps. */}
                <input
                  type="search"
                  placeholder="Search projects…"
                  value={projectQuery}
                  onChange={(e) => setProjectQuery(e.target.value)}
                  aria-label="Search projects"
                  className={cn("projects-page").elem("search").toClassName()}
                />
                {/* Create lives HERE — rightmost of the workspace-filter row —
                    not in the page header, so the My projects / Discover tab
                    row above never has to share its line with an action and
                    stays put on every width. */}
                {canCreateProjects && showCreateButton && activeTab === "projects" && (
                  <Button
                    onClick={openModal}
                    className={cn("projects-page").elem("create-btn").toClassName()}
                    aria-label="Create new project"
                    disabled={atProjectLimit}
                    title={createTooltip}
                  >
                    <IconPlus />
                    {/* Full label on desktop, short "Create" on phones so the
                        chips + button share one row without wrapping. */}
                    <span className={cn("projects-page").elem("create-label-long").toClassName()}>
                      {t("pages.projects.projects.create_project")}
                    </span>
                    <span className={cn("projects-page").elem("create-label-short").toClassName()}>Create</span>
                  </Button>
                )}
                {/* Toggle renders here only for non-managers (who don't
                    see the tabs row above). Managers get it inside the
                    tabs row instead, for a single visual anchor point. */}
                {!canCreateProjects && (
                  <div
                    className={cn("projects-page").elem("view-toggle").toClassName()}
                    role="group"
                    aria-label="View mode"
                  >
                    <button
                      type="button"
                      className={cn("projects-page")
                        .elem("view-btn")
                        .mod({ active: view === "list" })
                        .toClassName()}
                      onClick={() => setView("list")}
                      aria-label="Switch to list view"
                      aria-pressed={view === "list"}
                      title="List view"
                    >
                      <IconList />
                    </button>
                    <button
                      type="button"
                      className={cn("projects-page")
                        .elem("view-btn")
                        .mod({ active: view === "grid" })
                        .toClassName()}
                      onClick={() => setView("grid")}
                      aria-label="Switch to grid view"
                      aria-pressed={view === "grid"}
                      title="Grid view"
                    >
                      <IconGrid />
                    </button>
                  </div>
                )}
              </div>
            )}
            {projectsList.length ? (
              <ProjectsList
                projects={
                  workspaceFilter === "all" || workspaceFilter === "archived"
                    ? projectsList
                    : workspaceFilter === "none"
                      ? projectsList.filter((p) => !p.workspace)
                      : projectsList.filter((p) => String(p.workspace) === workspaceFilter)
                }
                view={view}
                currentPage={currentPage}
                totalItems={totalItems}
                loadNextPage={loadNextPage}
                pageSize={effectivePageSize}
                pageSizeOptions={view === "grid" ? [] : [10, 30, 50, 100]}
                onProjectUpdate={(id, updates) =>
                  setProjectsList((prev) => prev.map((p) => (p.id === id ? { ...p, ...updates } : p)))
                }
              />
            ) : search ? (
              <p className={cn("projects-page").elem("no-match").toClassName()} data-testid="projects-no-match">
                No project matches "{search}".
              </p>
            ) : archivedMode ? (
              <p className={cn("projects-page").elem("no-match").toClassName()} data-testid="projects-none-archived">
                No archived projects.
              </p>
            ) : (
              <EmptyProjectsList
                openModal={openModal}
                canCreateProjects={canCreateProjects}
                atProjectLimit={atProjectLimit}
                projectsMax={quota?.usage?.projects?.max}
                projectsCurrent={quota?.usage?.projects?.current}
                noActiveOrg={!user?.active_organization}
              />
            )}
            {modal && canCreateProjects && (
              <Suspense fallback={<Loading />}>
                <CreateProject onClose={closeModal} initialTemplate={null} />
              </Suspense>
            )}
          </div>
        </Oneof>
      )}
    </div>
  );
};

ProjectsPage.title = "Projects";
ProjectsPage.path = "/projects";
/** 12 cards on desktop, 8 on tablets, 6 on phones — tracks the viewport. */
function useGridPageSize() {
  const compute = () => {
    if (typeof window === "undefined") return 12;
    const w = window.innerWidth;
    if (w <= 600) return 6;
    if (w <= 1100) return 8;
    return 12;
  };
  const [size, setSize] = React.useState(compute);
  React.useEffect(() => {
    let t = 0;
    const onResize = () => {
      window.clearTimeout(t);
      t = window.setTimeout(() => setSize(compute()), 150);
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("resize", onResize);
    };
  }, []);
  return size;
}

ProjectsPage.exact = true;
ProjectsPage.routes = ({ store }) => [
  {
    title: () => store.project?.title,
    path: "/:id(\\d+)",
    exact: true,
    component: ProjectDashboard,
    context: ProjectDashboard.context,
    pages: {
      DataManagerPage,
      ExportPreviewPage,
      ValidatePage,
      SettingsPage,
      ReviewStreamPage,
      BulkLabelPage,
      MembersSettings,
      ProjectAnalytics,
    },
  },
];

// Bare ``/discover`` renders the very same merged page — ProjectsPage
// derives the active tab from the pathname, so this shows the Discover
// tab. Registered as its own page (see pages/index.js) so /discover is
// a real, directly-openable URL like the app's other tabs.
export const DiscoverRoutePage = () => <ProjectsPage />;
DiscoverRoutePage.title = "Discover";
DiscoverRoutePage.path = "/discover";
DiscoverRoutePage.exact = true;
// Create button used to live in the Menubar's right-hand context
// slot. Moved inline (right of the tab row) so it survives narrow
// layouts where the Menubar hides breadcrumbs + context items, and
// so the primary page action is adjacent to the content it acts on.
